// Drives `#initial-preloader` (the static markup in index.html) through its
// *entire* lifecycle — progress bar, error state, and the final fade-out —
// instead of handing off to SlotUI's own React <Preloader/> partway through.
//
// That handoff (App.tsx used to tear this element down the instant
// `reelsUrls` populated, right as SlotUI's <Preloader/> mounted underneath)
// was itself the bug: the two preloaders' markup had quietly drifted apart
// (glow blobs only in one, a pulse animation only in the other), so every
// drift showed up as a visible flicker at the exact swap moment. The fix
// isn't to chase pixel-parity forever — it's to not swap at all. This one
// static element now covers the whole loading window, and SlotUI's
// `preloader` prop is left off in App.tsx so a second overlay never mounts
// underneath it.
//
// Mirrors the event-matching, progress-smoothing, and hide-timing logic
// from Preloader.tsx (see that file for the fuller reasoning behind each
// piece) but as a plain module with no React dependency, imported at the
// very top of main.tsx so it starts listening before React even mounts —
// strictly earlier than the old React-effect-driven version ever could.
//
// SlotUI itself also refuses to boot the game inside a known in-app
// browser (see webviewDetect.ts + SlotUI.tsx's `blockInAppBrowsers`) —
// that's what actually matters for real consumers. But *this* module is
// the one thing standing between the player and a stuck spinner in this
// demo shell specifically: App.tsx deliberately never mounts SlotUI's own
// <Preloader/> (this static element covers the whole loading window
// instead), and the static element only ever hides on a `game.loaded`
// signal. In the blocked case no game ever loads, so without the check
// below the player would be stuck watching the progress bar creep toward
// 98% forever, with SlotUI's own (correct) warning message rendered but
// invisible underneath this one.
import { isInAppBrowser, hasInAppBrowserParam } from './webviewDetect';

const LOADED_EVENT_VARIANTS = ['game.loaded', 'GAME_LOADED', 'gameLoaded'].map((s) =>
  s.toLowerCase(),
);

// Same defaults App.tsx used to pass into <Preloader preloader={{ label:
// 'Loading…', minVisibleMs: 400 }}/> (postLoadDelayMs/fadeMs were left at
// Preloader.tsx's own defaults, so those are copied here too).
const MIN_VISIBLE_MS = 400;
const POST_LOAD_DELAY_MS = 500;
const FADE_MS = 400;

const startedAt = Date.now();
let triggered = false;
let targetProgress = 0;
let displayProgress = 0;
let rafHandle = 0;

function matchesLoadedEvent(raw: unknown): boolean {
  if (typeof raw === 'string') return LOADED_EVENT_VARIANTS.includes(raw.toLowerCase());
  if (raw && typeof raw === 'object') {
    const t = (raw as { type?: unknown }).type;
    if (typeof t === 'string') return LOADED_EVENT_VARIANTS.includes(t.toLowerCase());
  }
  return false;
}

function updateProgressDom(percent: number) {
  const fill = document.getElementById('initial-preloader-fill');
  const knob = document.getElementById('initial-preloader-knob');
  if (!fill || !knob) return;
  const clamped = Math.max(0, Math.min(100, percent));
  fill.style.width = `${clamped}%`;
  knob.style.left = `${clamped}%`;
  knob.textContent = `${Math.round(clamped)}%`;
}

// Same easing shape as Preloader.tsx's smoothing effect: ease quickly
// toward a real, higher target; once caught up, trickle slowly toward
// target+12 (capped at 98) so the bar still reads as "alive" during the
// dead zone between the last resource-loaded event and game.loaded.
function tick() {
  const dt = 16.7; // rAF is ~60fps; no real benefit to measuring actual dt here
  if (displayProgress < targetProgress) {
    const step = Math.max((targetProgress - displayProgress) * 0.1, 0.08) * (dt / 16.7);
    displayProgress = Math.min(displayProgress + step, targetProgress);
  } else if (!triggered) {
    const ceiling = Math.min(targetProgress + 12, 98);
    displayProgress = Math.min(displayProgress + 0.003 * dt, ceiling);
  } else {
    displayProgress = Math.min(displayProgress + 0.003 * dt, 100);
  }
  updateProgressDom(displayProgress);
  if (displayProgress < 100) {
    rafHandle = requestAnimationFrame(tick);
  }
}
function showError(message: string, title = 'Failed to load') {
  const el = document.getElementById('initial-preloader');
  const titleEl = document.getElementById('initial-preloader-error-title');
  const msg = document.getElementById('initial-preloader-error-message');
  if (!el || !titleEl || !msg) return;
  titleEl.textContent = title;
  msg.textContent = message;
  el.classList.add('has-error');
}

function hide() {
  cancelAnimationFrame(rafHandle);
  const el = document.getElementById('initial-preloader');
  if (!el) return;
  el.style.transition = `transform ${FADE_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`;
  el.style.transform = 'translateY(-100%)';
  el.style.pointerEvents = 'none';
  setTimeout(() => el.remove(), FADE_MS);
}

function beginHide() {
  if (triggered) return;
  triggered = true;
  targetProgress = 100;
  const row = document.getElementById('initial-preloader-progress-row');
  if (row) row.style.opacity = '0';

  const elapsed = Date.now() - startedAt;
  const wait = Math.max(0, MIN_VISIBLE_MS - elapsed) + POST_LOAD_DELAY_MS;
  setTimeout(hide, wait);
}

// Cross-origin iframes (the hosted games run on a different port/origin in
// dev, and typically a different path in prod) can't dispatch same-origin
// document events, so postMessage is the channel that matters in practice —
// but same-origin document events are still accepted for parity with
// Preloader.tsx / ReelsStack.
const onMessage = (e: MessageEvent) => {
  const d = e.data;
  if (matchesLoadedEvent(d)) {
    beginHide();
    return;
  }
  if (d && typeof d === 'object') {
    if (d.type === 'loading.progress' && typeof d.percent === 'number') {
      targetProgress = Math.max(targetProgress, d.percent);
    }
    if (d.type === 'loading.error') {
      showError(typeof d.message === 'string' && d.message ? d.message : 'Failed to load game');
    }
  }
};

if (isInAppBrowser() || hasInAppBrowserParam()) {
  // No game will ever load here — skip the progress rAF and the
  // game.loaded listeners entirely and go straight to the (permanent)
  // error state instead of animating a progress bar toward nothing.
  showError(
    'This game can’t run inside this app’s built-in browser. Tap the menu (⋯ or ⋮) and choose “Open in Browser” to continue.',
    'Open in your browser',
  );
} else {
  rafHandle = requestAnimationFrame(tick);
  window.addEventListener('message', onMessage);
  LOADED_EVENT_VARIANTS.forEach((v) => document.addEventListener(v, beginHide));
}
